import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CompanyWithCounts } from '@mvcp/contracts';
import { createCompany, listCompanies } from '../api';
import { MAX_TEXT_LEN } from '../lib/field-limits';

import { FormModal } from './FormModal';
import { Logo } from './Logo';
const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; companies: CompanyWithCounts[] }
  | { kind: 'error'; message: string };

/**
 * Admin Console → Companies (Story 1.4, FR-29). Lists Companies with per-Company
 * Sites/Users counts (mono numerics) and a create form. Admin-only surface —
 * belt with the API's `@Roles('system_administrator')` guard.
 */
export function CompaniesView(): React.JSX.Element {
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [maritime, setMaritime] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** Whether the onboard dialog is open (create moved out of the page body). */
  const [onboarding, setOnboarding] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const companies = await listCompanies();
      setState({ kind: 'ready', companies });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await createCompany({ name, country: country || undefined, maritime });
      setName('');
      setCountry('');
      setMaritime(false);
      // Close on SUCCESS only — a failure keeps the dialog and the typed values.
      setOnboarding(false);
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Companies
        </h1>
        <button
          type="button"
          className="btn-accent"
          data-surface="accent"
          onClick={() => {
            setFormError(null);
            setOnboarding(true);
          }}
        >
          Onboard a Company
        </button>
      </div>

      {onboarding && (
      <FormModal title="Onboard a Company" titleId="new-company-heading" onClose={() => setOnboarding(false)}>
        <form
          onSubmit={onCreate}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}
        >
          <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
            <span className="field-label">Name</span>
            <input
              aria-label="Company name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_TEXT_LEN}
              style={fieldStyle}
            />
          </label>
          {/* `maxLength={2}` finally makes the placeholder true (DEV-4426 round 2, QA comment
              46197 Topic 2): the field has always said "e.g. GR" and always accepted a
              sentence. Ergonomic only, exactly as `field-limits.ts` describes — the server
              validates membership in the ISO list, so this stops the overflow rather than
              deciding the rule, and `ZZ` is refused there rather than here. */}
          <label className="stack" style={{ gap: 4, flex: '0 1 120px' }}>
            <span className="field-label">Country</span>
            <input
              aria-label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. GR"
              maxLength={2}
              style={fieldStyle}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={maritime}
              onChange={(e) => setMaritime(e.target.checked)}
            />
            Maritime
          </label>
          <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create Company'}
          </button>
        </form>
        <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
          {formError && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {formError}
            </span>
          )}
        </div>
      </FormModal>
      )}

      <section className="card stack" aria-labelledby="companies-heading">
        <h2 id="companies-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          All Companies
        </h2>

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && (
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
        )}

        {state.kind === 'ready' && state.companies.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No companies yet — onboard one above.</p>
        )}

        {state.kind === 'ready' && state.companies.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Maritime</th>
                  <th data-align="right">Sites</th>
                  <th data-align="right">Users</th>
                </tr>
              </thead>
              <tbody>
                {state.companies.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="cell-identity">
                        <Logo id={c.id} name={c.name} logo={c.logo} />
                        {c.name}
                      </span>
                    </td>
                    <td data-numeric>{c.country ?? '—'}</td>
                    <td>
                      <span className="status-pill" data-tone={c.maritime ? 'info' : 'neutral'}>
                        <span className="status-pill__dot" aria-hidden="true" />
                        {c.maritime ? 'Maritime' : 'Non-maritime'}
                      </span>
                    </td>
                    <td data-align="right" data-numeric>
                      {c.sitesCount}
                    </td>
                    <td data-align="right" data-numeric>
                      {c.usersCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
